import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";

import FlexControlledCheckboxSeparatedLabel from "../../../../components/controlled/FlexControlledCheckboxSeparatedLabel";
import FlexControlledDatePickerSeparatedLabel from "../../../../components/controlled/FlexControlledDatePickerSeparatedLabel";
import FlexControlledRadioGroupSeparatedLabel from "../../../../components/controlled/FlexControlledRadioGroupSeparatedLabel";
import NewControlledTextFieldSeparatedLabel from "../../../../components/controlled/FlexControlledTextFieldSeparatedLabel";

import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { Controller } from "react-hook-form";

import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
	useGetCompanyWithIdPaymentInfoQuery,
	useUpdateCompanyWithIdPaymentInfoMutation,
} from "../../../../features/companies/companiesApiSlice";

const PaymentInfo = ({ companyId }) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [updatePaymentInfo, { isLoading: isUpdating }] =
		useUpdateCompanyWithIdPaymentInfoMutation();
	const {
		data: info,
		error,
		isLoading,
	} = useGetCompanyWithIdPaymentInfoQuery(companyId);

	const {
		handleSubmit,
		control,
		setValue,
		formState: { errors },
	} = useForm({
		// resolver: yupResolver(formSchema),
		shouldFocusError: true,
	});

	useEffect(() => {
		if (info) {
			setValue("userCount", info.userCount);
			setValue("perUserFee", info.perUserFee);
			setValue("baseFee", info.baseFee);
			setValue("monthSum", info.monthSum);
			setValue("paymentCycle", info.paymentCycle);
			setValue("isTesting", info.isTesting);
			setValue("isBlockedForFv", info.isBlockedForFv);
			setValue("contractDate", info.contractDate);
			setValue("eFvAddress", info.eFvAddress);
			setValue("isActive", info.isActive ? "1" : "0");
			setValue("aiRequestsPerUser", info.aiRequestsPerUser);
		}
	}, [info, setValue]);

	const onSubmit = async ({
		contractDate,
		eFvAddress,
		isActive,
		isBlockedForFv,
		isTesting,
		paymentCycle,
		perUserFee,
		baseFee,
		monthSum,
		aiRequestsPerUser,
	}) => {
		await updatePaymentInfo({
			companyId,
			data: {
				contractDate,
				eFvAddress,
				isBlockedForFv,
				isTesting,
				paymentCycle,
				perUserFee,
				baseFee,
				monthSum,
				isActive: isActive === "1" ? true : false,
				aiRequestsPerUser,
			},
		})
			.unwrap()
			.then((_payload) => {
				enqueueSnackbar(t("updated_data"), {
					variant: "success",
				});
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([_key, message]) => {
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	};

	if (isLoading || !info) {
		return <Skeleton variant="rectangular" height={274.71} />;
	}

	return (
        // background: "#F2F2F2"
        <Box sx={{ background: "#f2f2f2", display: "flex" }}>
            <Box
				component="form"
				noValidate
				sx={{ width: "100%", p: 5 }}
				onSubmit={handleSubmit(onSubmit)}
			>
				<Grid container spacing={5}>
					<Grid
						container
						spacing={2}
						size={{
							xs: 12,
							sm: 8,
							md: 8,
						}}
					>
						<Grid container spacing={5} size={12}>
							<Grid
								size={{
									md: 6,
								}}
							>
								<NewControlledTextFieldSeparatedLabel
									shrinkLabel={false}
									control={control}
									margin="none"
									name="userCount"
									label={t("users_count")}
									disabled
									required
									error={!!errors?.userCount}
									labelStyles={{ flex: "0 0 160px", justifyContent: "end" }}
									fullWidth
								/>
							</Grid>
							<Grid
								size={{
									md: 6,
								}}
							>
								<NewControlledTextFieldSeparatedLabel
									shrinkLabel={false}
									control={control}
									margin="none"
									name="perUserFee"
									label={t("payment_per_user")}
									required
									error={!!errors?.perUserFee}
									labelStyles={{ flex: "0 0 166px", justifyContent: "end" }}
									fullWidth
								/>
							</Grid>
						</Grid>
						<Grid container spacing={5} size={12}>
							<Grid
								size={{
									md: 6,
								}}
							>
								<NewControlledTextFieldSeparatedLabel
									shrinkLabel={false}
									control={control}
									margin="none"
									name="baseFee"
									label={t("base_fee")}
									required
									error={!!errors?.baseFee}
									labelStyles={{ flex: "0 0 160px", justifyContent: "end" }}
									fullWidth
								/>
							</Grid>
							<Grid
								size={{
									md: 6,
								}}
							>
								<NewControlledTextFieldSeparatedLabel
									shrinkLabel={false}
									control={control}
									margin="none"
									name="monthSum"
									label={t("month_sum_label")}
									required
									error={!!errors?.monthSum}
									labelStyles={{ flex: "0 0 126px", justifyContent: "end" }}
									fullWidth
								/>
							</Grid>
						</Grid>
						<Grid container spacing={5} size={12}>
							<Grid size="grow">
								<FlexControlledRadioGroupSeparatedLabel
									control={control}
									name="paymentCycle"
									label={t("payment_cycle")}
									options={[
										{ value: 3, label: "3M" },
										{ value: 6, label: "6M" },
										{ value: 12, label: "12M" },
									]}
									labelStyles={{ flex: "0 0 160px", justifyContent: "end" }}
									radioStyles={{ py: 1 }}
								/>
							</Grid>
							<Grid sx={{ display: "flex", justifyContent: "end" }} size="auto">
								<FlexControlledCheckboxSeparatedLabel
									control={control}
									margin="none"
									error={!!errors?.isTesting}
									name="isTesting"
									label={t("company_status_testing")}
									checkboxStyles={{ py: 1, pr: 0 }}
								/>
							</Grid>
						</Grid>
						<Grid container spacing={5} size={12}>
							<Grid
								sx={{ display: "flex", alignItems: "center" }}
								size={{
									md: 6,
								}}
							>
								<FlexControlledCheckboxSeparatedLabel
									control={control}
									margin="none"
									error={!!errors?.blockedForFv}
									name="isBlockedForFv"
									label={t("company_status_invoice_blocked")}
									labelStyles={{ flex: "0 0 160px", justifyContent: "end" }}
									checkboxStyles={{ py: 0, pr: 0 }}
								/>
							</Grid>
							<Grid
								sx={{ display: "flex", justifyContent: "end" }}
								size={{
									md: 6,
								}}
							>
								<FlexControlledDatePickerSeparatedLabel
									control={control}
									margin="normal"
									name="contractDate"
									label={t("contract_date_label")}
								/>
							</Grid>
						</Grid>
						<Grid container spacing={5} size={12}>
							<Grid
								sx={{ display: "flex", alignItems: "center" }}
								size={{
									md: 6,
								}}
							>
								<NewControlledTextFieldSeparatedLabel
									shrinkLabel={false}
									control={control}
									margin="none"
									name="aiRequestsPerUser"
									label={t("ai_limit_per_user")}
									required
									error={!!errors?.aiRequestsPerUser}
									fullWidth
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid
						size={{
							xs: 12,
							sm: 4,
							md: 4,
						}}
					>
						<Box
							sx={{
								borderLeft: "1px solid #BFBFBF",
								height: "100%",
								pl: "40px",
							}}
						>
							<NewControlledTextFieldSeparatedLabel
								shrinkLabel={false}
								control={control}
								margin="none"
								name="eFvAddress"
								label={t("e_fv")}
								required
								error={!!errors?.eFv}
								fullWidth
							/>
							<Controller
								control={control}
								name="isActive"
								render={({ field }) => {
									return (
										<Box>
											<RadioGroup column {...field} value={field.value || "0"}>
												<FormControlLabel
													value="1"
													control={<Radio />}
													label={t("active_status")}
												/>
												<FormControlLabel
													value="0"
													control={<Radio />}
													label={t("blocked_status")}
												/>
											</RadioGroup>
										</Box>
									);
								}}
							/>

							<Box>
								<Button
									loading={isUpdating}
									type="submit"
									fullWidth
									variant="contained"
								>
									{t("save")}
								</Button>
							</Box>
						</Box>
					</Grid>
				</Grid>
			</Box>
        </Box>
    );
};

export default PaymentInfo;
